import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { api, ApiError } from '../api/client';
import type { BatchSummary, FilterKey, JobSummary } from '../api/types';
import {
  FAILURE_CATEGORY_R_LABEL,
  filterToApiStates,
  isFailureCategoryR,
} from '../api/types';
import { BatchModal } from '../components/BatchModal';
import { BatchSummaryCard } from '../components/BatchSummaryCard';
import { BatchSummaryFeedCard } from '../components/BatchSummaryFeedCard';
import { EmptyState } from '../components/EmptyState';
import { Fab } from '../components/Fab';
import { JobCard, JobCardSkeleton } from '../components/JobCard';
import { PullToRefresh } from '../components/PullToRefresh';
import { SearchMeta, SearchRow } from '../components/SearchRow';
import { SeriesPrepCard } from '../components/SeriesPrepCard';
import { SummaryCells } from '../components/SummaryCells';
import { useBatches } from '../hooks/useBatches';
import { useBatchSummaries } from '../hooks/useBatchSummaries';
import { useDebounce } from '../hooks/useDebounce';
import { useJobs } from '../hooks/useJobs';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { useStatus } from '../hooks/useStatus';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';
import { isAnalyticsFailureDrill } from '../lib/analyticsDrillState';
import {
  type ActivePrep,
  listActivePreps,
  removeActivePrep,
} from '../lib/activePreps';
import { formatGnssList } from '../lib/gnss_labels';
import { matchesQuery, parseSearchQuery } from '../lib/search';
import { isLikelyStationCode } from '../lib/station_code_heuristic';

/** Phase 4.13.1 — discriminated union for the merged feed.
 *
 *  ``type='job'`` items render as ``<JobCard>`` (the existing
 *  Phase-3.2 card), ``type='batch_summary'`` as
 *  ``<BatchSummaryFeedCard>``. The merge runs in ``mergeFeedItems``
 *  newest-first by a per-item sort key. */
type FeedItem =
  | { type: 'job'; data: JobSummary; sortKey: string }
  | { type: 'batch_summary'; data: BatchSummary; sortKey: string };

/** Phase 4.13.1 fixup — surface the batch card near "when it
 *  finished", not "when it started".
 *
 *  Original anchor was ``first_created_at`` which put the card at
 *  the very BEGINNING of the batch's time slot. For batches that
 *  ran for hours (the night_smoke_4.14 case: 7h wall, with
 *  substitute jobs created mid-way as originals failed), this
 *  buried the card under every substitute and post-batch job. The
 *  card was technically in the feed but ~90 positions below the
 *  fold.
 *
 *  Fix: prefer ``last_job_finished_at`` (when the batch actually
 *  became terminal — close to "now" in operator time), falling
 *  back to ``last_activity_at`` for active-but-included batches,
 *  and finally ``first_created_at`` for batches with no activity
 *  beyond creation (shouldn't happen in practice but defended).
 *  The result: a completed batch surfaces at the time the operator
 *  finished interacting with it — which is the time slot they'd
 *  scroll back to for review.
 */
function batchSortKey(summary: BatchSummary): string {
  return (
    summary.last_job_finished_at ??
    summary.last_activity_at ??
    summary.first_created_at
  );
}

/** Spec A · Step 0 — stable React key for a batch card. Card identity
 *  is the composite (tag, obs_date, analysis_type): after the
 *  visibility fix one tag can legitimately yield several cards, so
 *  keying by tag alone would collide. Falls back gracefully when an
 *  older backend omits the new fields. */
export function batchCardKey(summary: BatchSummary): string {
  return `${summary.tag}|${summary.obs_date ?? ''}|${summary.analysis_type ?? ''}`;
}

export function mergeFeedItems(
  jobs: JobSummary[],
  summaries: BatchSummary[],
): FeedItem[] {
  const items: FeedItem[] = [
    ...jobs.map(
      (j): FeedItem => ({ type: 'job', data: j, sortKey: j.created_at }),
    ),
    ...summaries.map(
      (s): FeedItem => ({
        type: 'batch_summary',
        data: s,
        sortKey: batchSortKey(s),
      }),
    ),
  ];
  // Newest first. ISO 8601 strings sort lexicographically the same
  // as chronologically as long as both carry the ``+00:00`` suffix
  // — the API enforces that via ``_serialize_utc_iso``.
  items.sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  return items;
}

/** The Mini App's home screen — the only fully-implemented screen in
 *  Phase 3.2. Wires together SummaryCells / SearchRow / FilterTabs /
 *  JobCard plus the pull-to-refresh container. State that lives here:
 *
 *  * ``filter`` — the active category (also driven by SummaryCells clicks).
 *  * ``searchOpen`` / ``search`` — the search input state; the API
 *    request is debounced 300 ms so we don't fan out one fetch per
 *    keystroke.
 */
export function FeedScreen() {
  const { data: statusData } = useStatus();

  // Phase R — pull the failure-category breakdown so the strip
  // below the legacy summary cells shows an honest split. Cheap
  // aggregate query, 30 s staleTime matches the rest of the Mini
  // App's analytics cadence.
  const { data: failureBreakdown } = useQuery({
    queryKey: ['failure-breakdown'],
    queryFn: () => api.getFailureBreakdown(),
    staleTime: 30_000,
  });
  const [batchOpen, setBatchOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { tg } = useTelegramWebApp();
  const location = useLocation();

  // C4a.1 — Analytics drill-down origin, captured ONCE on mount from
  // location.state (a machine marker, never the URL). Captured (not read live)
  // so it survives an in-Feed filter change / clear, while a shared /
  // deep-linked ?failure_category= link (no state) never gets a false
  // return-context and a hard refresh correctly drops it. A Feed→Detail→Back
  // round-trip restores it (react-router preserves the drill entry's state).
  const [analyticsDrill] = useState(() => isAnalyticsFailureDrill(location.state));

  // C4a.1 — scoped Telegram BackButton: ONLY when the Feed was opened by an
  // Analytics failure-category drill. Reuses the DetailScreen pattern (show +
  // onClick → offClick + hide), scoped to this screen — no global hook. The
  // effect runs exactly once (analyticsDrill / tg / navigate are all stable),
  // so re-renders never accumulate listeners; cleanup offClicks the SAME
  // handler and hides on unmount (Feed→Detail / Feed→Analytics).
  useEffect(() => {
    if (!analyticsDrill || !tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate('/analytics');
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton?.offClick(onBack);
      tg.BackButton?.hide();
    };
  }, [analyticsDrill, tg, navigate]);

  // Phase 3.6: ``?tag=…`` query param drives an auto-applied filter.
  // The batch-completion notification's "🗺 Открыть в Mini App"
  // button deep-links the user here with their batch's tag set. A
  // ✕ in the badge below removes the param. Phase 3.14 — the
  // BatchSummaryCard reuses the same hook: clicking a card pushes
  // ``?tag=<tag>`` and the existing badge / filtering path takes over.
  //
  // Phase 4.11 — ``?q=…`` joins the same searchParams object so the
  // search input survives back-nav from JobDetail. Both the tag and
  // the query are URL state now; clearing one doesn't touch the other.
  const [searchParams, setSearchParams] = useSearchParams();
  // Bug-fix (nav) — the state-tab filter (all/pending/active/done/failed)
  // now lives in the URL (``?filter=done``) instead of component state, so
  // that ``navigate(-1)`` from a job detail restores the tab the operator
  // was on. Previously this was a ``useState`` that reset to «all» on every
  // FeedScreen remount, dropping the operator back into «ВСЕГО».
  const filterParam = searchParams.get('filter');
  const filter: FilterKey = (
    ['all', 'pending', 'active', 'done', 'failed'] as const
  ).includes(filterParam as FilterKey)
    ? (filterParam as FilterKey)
    : 'all';
  const setFilter = (next: FilterKey) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        // «all» is the default — keep the URL clean by omitting it.
        if (next === 'all') params.delete('filter');
        else params.set('filter', next);
        return params;
      },
      { replace: true },
    );
  };
  const apiStates = filterToApiStates(filter);
  const tagFilter = searchParams.get('tag') ?? undefined;
  // Phase 6.1 — ``?station=AMC4`` filter. Mirrors the
  // ``?tag=`` pattern: read from the URL, pass to ``useJobs``,
  // render a clearable chip. The Analytics heatmap navigates here
  // when the operator clicks a station-name header.
  const stationFilter = searchParams.get('station') ?? undefined;
  // Phase 6.5 — three drill-down filters from the BatchCard expansion.
  // Same URL idiom as ``?station=`` above. The Feed renders a
  // clearable chip for each (FilterChips block below) and forwards
  // the value to ``useJobs`` for server-side filtering.
  const failedCategoryFilter = searchParams.get('failed_category') ?? undefined;
  // Phase R — operator-facing R-taxonomy bucket (`?failure_category=`).
  // Separate URL parameter from the legacy `?failed_category=`
  // (V2 6-bucket taxonomy used by the BatchCard drill-down) so
  // both filters can coexist without one silently overriding the
  // other. REPEATABLE (OR semantics) so the «к разбору» drill-in can
  // pass service_failed + other in one shot; a single value still works.
  // C4a — validate at the input boundary: drop any unknown/obsolete key from a
  // stale deep-link / old bookmark / hand-edited URL so it never reaches the
  // filter state, the jobs request, or the UI chip. Only machine
  // ``FailureCategoryR`` keys survive.
  const failureCategoryFilter = searchParams
    .getAll('failure_category')
    .filter(isFailureCategoryR);
  const residualSkipFilter =
    searchParams.get('residual_skip') === 'true' ? true : undefined;
  const configFilter = searchParams.get('config') ?? undefined;
  const search = searchParams.get('q') ?? '';
  // Phase 4.11 — the search input is "open" any time the query is
  // non-empty. Toggling closed clears the query (and the URL param);
  // re-opening it from an empty state stays empty until the operator
  // types something.
  const [searchOpenManual, setSearchOpenManual] = useState(false);
  const searchOpen = searchOpenManual || search.length > 0;
  const debouncedSearch = useDebounce(search, 300);
  const trimmedSearch = debouncedSearch.trim();

  const setSearch = (next: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next.trim()) {
          params.set('q', next);
        } else {
          params.delete('q');
        }
        return params;
      },
      { replace: true },
    );
  };

  // Phase 4.11 — recent searches history (localStorage). Committed
  // on Enter inside the input AND on picks from the dropdown
  // (clicking an entry re-adds it as most-recent).
  const { history, addToHistory } = useSearchHistory();

  // Phase 3.14 — active-batches dashboard. Pull only batches with
  // in-flight jobs (``include_completed=false`` default) and render
  // the top 3 above the jobs list. ``useBatches`` polls every 30 s
  // so progress ticks visibly while the screen is open. Hidden
  // entirely when the list is empty — no "no active batches"
  // placeholder, since "no batches" is normal idle state.
  const batchesQuery = useBatches();
  const activeBatches = (batchesQuery.data ?? []).slice(0, 3);

  // Wave 4b — async-convergence «готовлю N/M» cards. The registry is
  // localStorage-backed (survives a Telegram WebView reload); we mirror
  // it into state so adding/removing re-renders the section. Seeded from
  // storage on mount so a reload mid-prep restores the cards.
  const [activePreps, setActivePreps] = useState<ActivePrep[]>(() =>
    listActivePreps(),
  );
  const handlePrepDone = (batchId: string) => {
    removeActivePrep(batchId);
    setActivePreps(listActivePreps());
    // The freshly-built series are now real batches/jobs — pull them in
    // so the prep card hands off seamlessly to a BatchSummaryCard.
    qc.invalidateQueries({ queryKey: ['batches'] });
    qc.invalidateQueries({ queryKey: ['jobs'] });
  };

  // Phase 3.14.1 — collapsed state persists across reloads so an
  // operator who works mostly in the jobs feed doesn't have to
  // re-collapse the dashboard every open. Default = expanded.
  // ``try/catch`` shields against Safari private mode where
  // localStorage throws on access.
  const [batchesCollapsed, setBatchesCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('gnss-qa:active-batches-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        'gnss-qa:active-batches-collapsed',
        String(batchesCollapsed),
      );
    } catch {
      /* ignore storage failures */
    }
  }, [batchesCollapsed]);

  // Phase 4.11 — the server-side ``?search=`` filter does a
  // substring ``LIKE`` over tag / station_code / id. When the user
  // wraps their query in quotes for exact-match, we strip the
  // quotes BEFORE sending to the API (otherwise the server gets
  // ``"abc"`` and finds nothing); the exact-match step is applied
  // client-side via ``matchesQuery`` on the returned items.
  const parsedQuery = parseSearchQuery(trimmedSearch);
  const jobsQuery = useJobs({
    state: apiStates,
    search: parsedQuery.apiSearch || undefined,
    tag: tagFilter,
    station: stationFilter,
    // Phase 6.3 — drill-down filters from BatchCard (taxonomy v2).
    failed_category: failedCategoryFilter as
      | 'rinex_real_missing'
      | 'rinex_parse_error'
      | 'rinex_not_available'
      | 'compute_failed_ambiguity'
      | 'service_compute_failed'
      | 'other'
      | undefined,
    // Phase R — independent R-taxonomy filter. Indexed membership
    // on Job.failure_category (OR across the given buckets), no Python
    // post-filter. Empty array → omitted (no filter).
    failure_category: failureCategoryFilter.length
      ? failureCategoryFilter
      : undefined,
    residual_skip: residualSkipFilter,
    config: configFilter,
    limit: 100,
  });

  // Phase 4.13.1 — completed batch summaries for the inline feed
  // cards. Active series remain in the top-of-feed dashboard via
  // ``useBatches``; this hook fetches the COMPLETED-only set so the
  // two paths don't dup the same tag.
  //
  // Phase 4.13.1 fixup — when ``?tag=…`` is in the URL (operator
  // drilled into one batch via the active-batches dashboard or the
  // FeedScreen tag badge), NARROW the summaries to that tag rather
  // than hiding them entirely. The original "blank when tagFilter"
  // logic accidentally hid the SAME batch the operator drilled
  // into. Showing only the matching summary (instead of all OR
  // none) keeps the card visible at the top while preserving the
  // "no duplication" invariant of Phase 4.13.1.
  const summariesQuery = useBatchSummaries({
    state: 'completed',
    limit: 50,
  });
  //
  // Phase 7.2 — gate completed summaries behind the «все» filter.
  // The job-state filters ('active' / 'pending' / 'failed' / 'done')
  // narrow the feed to JOBS in that state; a completed batch
  // summary is a *series* artefact, not a job, so it must not leak
  // into a filtered view. Before this gate, picking «Активны»
  // rendered finished «… · ЗАВЕРШЕНА» series cards alongside the
  // genuinely-active jobs (reported bug). Completed series belong
  // only in the unfiltered timeline; `filter !== 'all'` returns an
  // empty list so `mergeFeedItems` merges jobs only.
  const completedSummaries = useMemo(() => {
    if (filter !== 'all') return [];
    const all = summariesQuery.data ?? [];
    if (!tagFilter) return all;
    return all.filter((s) => s.tag === tagFilter);
  }, [summariesQuery.data, tagFilter, filter]);

  // Server-returned page (already substring-filtered + state-filtered + tag-filtered).
  const rawItems = jobsQuery.data?.items ?? [];
  // Phase 4.11 — client-side post-filter. A no-op when the user
  // didn't wrap their query in quotes; an exact-match narrowing
  // when they did. Memoised so the JobCard list doesn't re-render
  // on unrelated state changes.
  const items = useMemo(
    () => rawItems.filter((j) => matchesQuery(j, trimmedSearch)),
    [rawItems, trimmedSearch],
  );

  // "found X of Y" — X is the filtered-and-searched page count, Y is
  // the project's overall total from /status (unfiltered) so the user
  // sees what proportion of the whole database matched.
  const totalAllJobs = statusData?.counts.total ?? 0;
  const showSearchMeta = searchOpen && trimmedSearch.length > 0;
  const showSkeleton = jobsQuery.isLoading && items.length === 0;
  const showEmpty = !jobsQuery.isLoading && items.length === 0;

  // Phase 4.13.1 — merge jobs + completed batch summaries by
  // created_at so a series-card sits in the feed at its time slot.
  // The merge only runs once both queries have data; while jobs is
  // loading we render skeletons (no summaries either — keeps the
  // initial paint coherent).
  const rawFeedItems = useMemo(
    () => mergeFeedItems(items, completedSummaries),
    [items, completedSummaries],
  );

  // Phase 6.7 C — «query похожа на station code» — drop batch-
  // summary cards from the result list when the operator is
  // clearly hunting for a station. Without this, a search like
  // `FRDN` ends up showing a Random25 batch-card at the top
  // (because that batch contains a job using that station) and
  // pushes the actual FRDN job-card down. The heuristic is
  // intentionally narrow (4 chars of [A-Z0-9], at least one
  // letter) so 4-digit job-id hunts and arbitrary tag searches
  // pass through unchanged.
  //
  // Hint text below the search row tells the operator why series
  // are hidden, so the filter doesn't look like a bug.
  const stationCodeHeuristic = isLikelyStationCode(trimmedSearch);
  const hiddenSeriesCount = stationCodeHeuristic
    ? rawFeedItems.filter((item) => item.type === 'batch_summary').length
    : 0;
  // «К разбору» drill-down — show ONLY station (job) cards, no series
  // cards between them. A failure_category filter is a flat «problem
  // stations» view; a series card showing «отменено 30» there is noise
  // (and misleading, since CANCELLED isn't «к разбору»). The series tag
  // stays on each station card as context. Series remain in the normal feed.
  const reviewDrillActive = failureCategoryFilter.length > 0;
  const feedItems = useMemo(() => {
    if (!stationCodeHeuristic && !reviewDrillActive) return rawFeedItems;
    return rawFeedItems.filter((item) => item.type !== 'batch_summary');
  }, [rawFeedItems, stationCodeHeuristic, reviewDrillActive]);

  // Phase 6.7 A — identify the freshest visible series so the
  // BatchSummaryFeedCard initialises that one expanded by default.
  // «Freshest» = MAX(first_created_at) among batch_summary items in
  // the merged feed; this works at the same time for the
  // unfiltered list and for narrow filtered views (whichever set
  // is on-screen carries its own freshest). Stale-time semantics
  // are fine — the prop is read only by useState's lazy
  // initialiser, so a later re-render doesn't flip the expand
  // state out from under the operator.
  const freshestBatchTag = useMemo(() => {
    let freshest: { tag: string; ts: string } | null = null;
    for (const item of feedItems) {
      if (item.type !== 'batch_summary') continue;
      const ts = item.data.first_created_at;
      if (!ts) continue;
      if (freshest === null || ts > freshest.ts) {
        freshest = { tag: item.data.tag, ts };
      }
    }
    return freshest?.tag ?? null;
  }, [feedItems]);

  return (
    <>
      {/* Feed redesign — the four big counters double as the feed state
          filter (all/pending/active/done); the active one gets a thin
          underline. The fifth «к разбору» counter is NOT a filter: it
          drops a breakdown popover (variant B). Its value =
          service_failed + other from failure-breakdown (the popover owns
          the open state internally). */}
      <SummaryCells
        counts={statusData?.counts}
        breakdown={failureBreakdown}
        activeFilter={filter}
        onCellClick={setFilter}
        onReviewDrill={(categories) => {
          // Drill the feed into the «к разбору» list. Replace any existing
          // failure_category params with the chosen bucket(s); state tab
          // goes to «all» so the category filter isn't shadowed by a
          // pending/done tab. The chip below clears it.
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              params.delete('filter');
              params.delete('failure_category');
              for (const c of categories) params.append('failure_category', c);
              return params;
            },
            { replace: true },
          );
        }}
      />

      <SearchRow
        open={searchOpen}
        value={search}
        history={history}
        onToggle={() => {
          if (searchOpen) {
            // Collapsing — clear both the URL query and the manual flag.
            setSearch('');
            setSearchOpenManual(false);
          } else {
            // Expanding — manual flag flips on; query stays empty
            // until the user types (or picks from history).
            setSearchOpenManual(true);
          }
        }}
        onChange={setSearch}
        onCommit={addToHistory}
        onPickFromHistory={(q) => {
          setSearch(q);
          addToHistory(q);
        }}
      />
      {showSearchMeta && <SearchMeta found={items.length} total={totalAllJobs} />}

      {/* Phase 6.7 C — station-code search hint. Renders below the
          search row when the query trips the heuristic AND at
          least one batch-summary card was hidden as a result.
          Tells the operator WHY they don't see series for a query
          that semantically looks like a tag — without negative
          phrasing. ``hiddenSeriesCount === 0`` (no series existed
          to hide) silently elides the hint so it doesn't appear on
          screens with only job-cards anyway. */}
      {stationCodeHeuristic && hiddenSeriesCount > 0 && (
        <div
          className="station-code-hint"
          role="status"
          data-testid="station-code-hint"
        >
          показаны только задачи (запрос похож на код станции) ·
          скрыто карточек серий: {hiddenSeriesCount}
        </div>
      )}

      {/* Bug-fix (scroll) — the active-series sections and the filter
          chips now live INSIDE the scroll container (PullToRefresh)
          together with the job list. Previously they were fixed flex
          siblings ABOVE the only scroll area, so an expanded active-series
          block consumed the viewport and squeezed the list's scroll
          region to ~0 («can't scroll to the list»). Scrolling them away
          with the list is the operator's mental model. SummaryCells +
          SearchRow stay pinned (they're the filter/nav). */}
      <PullToRefresh onRefresh={jobsQuery.refetch}>
      {/* In the «к разбору» drill-down the active-series blocks are also
          hidden — it's a flat problem-stations list, not the dashboard. */}
      {!reviewDrillActive && activePreps.length > 0 && (
        <section className="active-batches-section" data-expanded>
          <div className="section-head" aria-hidden>
            Создаются серии
          </div>
          <div>
            {activePreps.map((p) => (
              <SeriesPrepCard
                key={p.batch_id}
                batchId={p.batch_id}
                total={p.total}
                onDone={handlePrepDone}
              />
            ))}
          </div>
        </section>
      )}

      {!reviewDrillActive && activeBatches.length > 0 && (
        <section
          className="active-batches-section"
          data-expanded={!batchesCollapsed}
        >
          <button
            type="button"
            className="section-head"
            onClick={() => setBatchesCollapsed((c) => !c)}
            aria-expanded={!batchesCollapsed}
            aria-controls="active-batches-list"
          >
            {batchesCollapsed
              ? renderCompactSummary(activeBatches)
              : 'Активные серии'}
          </button>
          {!batchesCollapsed && (
            <div id="active-batches-list">
              {activeBatches.map((b) => (
                <BatchSummaryCard
                  key={batchCardKey(b)}
                  batch={b}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    next.set('tag', b.tag);
                    setSearchParams(next, { replace: true });
                  }}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {tagFilter && (
        <div className="tag-filter-badge" role="status">
          <span className="label">Серия:</span>
          <code className="tag">{tagFilter}</code>
          <button
            type="button"
            className="clear"
            aria-label="Снять фильтр серии"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('tag');
              setSearchParams(next, { replace: true });
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Phase 6.1 — station filter chip. Reuses the
          .tag-filter-badge styles so the two URL-driven chips
          look identical. Driven by ?station=<code> set by the
          Analytics heatmap row-header click. */}
      {stationFilter && (
        <div
          className="tag-filter-badge"
          role="status"
          data-testid="station-filter-badge"
        >
          <span className="label">Станция:</span>
          <code className="tag">{stationFilter}</code>
          <button
            type="button"
            className="clear"
            aria-label="Снять фильтр станции"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('station');
              setSearchParams(next, { replace: true });
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Phase 6.5 A — failed_category filter chip. Driven by a
          click on a row in the BatchCard «ОШИБКИ» block. The
          three label spellings match the spec §A.3 categories
          one-to-one — kept locally instead of in a global lookup
          because the only consumer is this chip. */}
      {failedCategoryFilter && (
        <div
          className="tag-filter-badge"
          role="status"
          data-testid="failed-category-filter-badge"
        >
          <span className="label">Тип ошибки:</span>
          {/* Phase 6.3 — taxonomy v2; six categories with parchment-+-ink
              palette. Same Russian wording as the BatchCard «ОШИБКИ»
              block (FAILED_CATEGORY_LABEL there) so the chip and the
              drill-down source spell the category identically. */}
          <code className="tag">
            {failedCategoryFilter === 'rinex_real_missing'
              ? 'нет систем в RINEX (RINEX корректный)'
              : failedCategoryFilter === 'rinex_parse_error'
                ? 'RINEX не разобрался'
                : failedCategoryFilter === 'rinex_not_available'
                  ? 'RINEX не скачан'
                  : failedCategoryFilter === 'compute_failed_ambiguity'
                    ? 'PPP не сошёлся по этой системе'
                    : failedCategoryFilter === 'service_compute_failed'
                      ? 'PPP вернул общий сбой'
                      : failedCategoryFilter === 'other'
                        ? 'прочее'
                        : failedCategoryFilter}
          </code>
          <button
            type="button"
            className="clear"
            aria-label="Снять фильтр категории ошибки"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('failed_category');
              setSearchParams(next, { replace: true });
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* C4a.1 — explicit «← В Аналитику» return, shown ONLY when the Feed was
          opened by an Analytics failure-category drill. Client-side (react-
          router <Link>, no raw <a href>); stays visible after the category
          filter is cleared, until the operator leaves this Feed entry. */}
      {analyticsDrill && (
        <Link
          to="/analytics"
          className="analytics-return-link"
          data-testid="analytics-return"
        >
          ← В Аналитику
        </Link>
      )}

      {/* «К разбору» drill-in chip — driven by the SummaryCells
          breakdown popover (one bucket, or the combined service_failed +
          other set). Clearable; one ✕ drops the whole drill. */}
      {failureCategoryFilter.length > 0 && (
        <div
          className="tag-filter-badge"
          role="status"
          data-testid="failure-category-filter-badge"
        >
          <span className="label">К разбору:</span>
          <code className="tag">
            {failureCategoryFilter
              .map((c) => FAILURE_CATEGORY_R_LABEL[c])
              .join(' + ')}
          </code>
          <button
            type="button"
            className="clear"
            aria-label="Снять фильтр «к разбору»"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('failure_category');
              setSearchParams(next, { replace: true });
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Phase 6.5 B — residual_skip filter chip. Driven by a
          click on the «N без эталона» badge in the BatchCard
          ТОЧНОСТЬ block. */}
      {residualSkipFilter && (
        <div
          className="tag-filter-badge"
          role="status"
          data-testid="residual-skip-filter-badge"
        >
          <span className="label">Без эталона:</span>
          <code className="tag">да</code>
          <button
            type="button"
            className="clear"
            aria-label="Снять фильтр «без эталона»"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('residual_skip');
              setSearchParams(next, { replace: true });
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Phase 6.5 C — config filter chip. Driven by a click on a
          row in the BatchCard ПО КОНФИГУРАЦИЯМ block. The
          canonical key (`gps`, `beidou,galileo,glonass,gps` …)
          is the URL value; we render the display label via
          ``formatGnssList`` for parity with the source surface. */}
      {configFilter && (
        <div
          className="tag-filter-badge"
          role="status"
          data-testid="config-filter-badge"
        >
          <span className="label">Конфигурация:</span>
          <code className="tag">
            {formatGnssList(configFilter.split(','))}
          </code>
          <button
            type="button"
            className="clear"
            aria-label="Снять фильтр конфигурации"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('config');
              setSearchParams(next, { replace: true });
            }}
          >
            ✕
          </button>
        </div>
      )}

        <div className="job-list">
          {showSkeleton && (
            <>
              <JobCardSkeleton />
              <JobCardSkeleton />
              <JobCardSkeleton />
            </>
          )}
          {jobsQuery.isError && (
            <EmptyState>
              {jobsQuery.error instanceof ApiError
                ? `ошибка ${jobsQuery.error.status}: ${jobsQuery.error.message}`
                : 'не удалось загрузить ленту'}
            </EmptyState>
          )}
          {showEmpty && !jobsQuery.isError && <EmptyState />}
          {feedItems.map((item) =>
            item.type === 'job' ? (
              <JobCard
                key={`job-${item.data.id}`}
                job={item.data}
                onClick={() => navigate(`/job/${item.data.id}`)}
              />
            ) : (
              <BatchSummaryFeedCard
                key={`batch-${batchCardKey(item.data)}`}
                summary={item.data}
                defaultExpanded={item.data.tag === freshestBatchTag}
              />
            ),
          )}
        </div>
      </PullToRefresh>

      <Fab onClick={() => setBatchOpen(true)} />

      <BatchModal
        open={batchOpen}
        onClose={() => setBatchOpen(false)}
        onCreated={(resp) => {
          setBatchOpen(false);
          qc.invalidateQueries({ queryKey: ['jobs'] });
          qc.invalidateQueries({ queryKey: ['status'] });
          // Phase 3.14 — refresh the active-batches dashboard so the
          // just-created batch surfaces immediately (otherwise the
          // user waits up to 30 s for the poll to catch up).
          qc.invalidateQueries({ queryKey: ['batches'] });
          tg?.HapticFeedback?.notificationOccurred('success');
          // «Не применимо» FIX 2 — surface configs skipped at generation
          // as cache-unreachable (no row created), so «Создано 0» reads
          // «пропущено недоступных: N» instead of looking like a no-op.
          const skipped = resp.unreachable_skipped ?? 0;
          const skippedNote = skipped > 0 ? `; пропущено недоступных: ${skipped}` : '';
          const msg =
            resp.created > 0
              ? `Создано задач: ${resp.created} (тег ${resp.tag})${skippedNote}`
              : `Новых задач 0; дубликатов: ${resp.skipped_duplicates}${skippedNote}`;
          if (tg?.showAlert) tg.showAlert(msg);
          else alert(msg);
        }}
        onConvergenceAccepted={(resp) => {
          // BatchModal already registered the batch + closed itself;
          // mirror the registry into state so the «готовлю N/M» card
          // appears at once, and nudge the batches poll.
          setActivePreps(listActivePreps());
          qc.invalidateQueries({ queryKey: ['batches'] });
          tg?.HapticFeedback?.notificationOccurred('success');
          const msg = `Принято. Готовлю ${resp.total} серий в фоне.`;
          if (tg?.showAlert) tg.showAlert(msg);
        }}
      />
    </>
  );
}

/** Phase 3.14.1 — compact one-line summary rendered in the collapsed
 *  "Активные серии" header. Singular form for a single batch ("Активная
 *  серия (96%)"), plural with averaged progress for multiple
 *  ("Активные серии (3 • средний прогресс 60%)"). The operator can
 *  glance at this without expanding the dashboard. */
function renderCompactSummary(batches: BatchSummary[]): string {
  const first = batches[0];
  if (batches.length === 1 && first) {
    return `Активная серия (${Math.round(first.progress_percent)}%)`;
  }
  const avg = Math.round(
    batches.reduce((acc, b) => acc + b.progress_percent, 0) / batches.length,
  );
  return `Активные серии (${batches.length} • средний прогресс ${avg}%)`;
}
